
class MultiColumnSlider extends window.BAO.CustomElement() {
  setupListeners () {
    this.slider = this.querySelector('[data-collection-carousel-slider]')
    this.controls = this.querySelector('[data-collection-carousel-controls]')
    this.dotsContainer = this.querySelector(
      '[data-collection-carousel-dots]',
    )
    this.progressBar = this.querySelector(
      '[data-collection-carousel-progress-bar]',
    )

    if (!this.slider) return

    this.enableSlider = this.dataset.enableSlider === 'true'
    this.columnGapMobile = Number(this.dataset.columnGapMobile)
    this.columnGapDesktop = Number(this.dataset.columnGapDesktop)
    this.columns = parseFloat(this.dataset.columns)

    if (!Number.isFinite(this.columns) || this.columns <= 0) {
      this.columns = 3
    }

    this.mobileColumns = parseFloat(this.dataset.mobileColumns)

    if (!Number.isFinite(this.mobileColumns) || this.mobileColumns <= 0) {
      this.mobileColumns = 2
    }

    this.stackMobile = this.dataset.stackMobile === 'true'
    this.paginationStyle = this.dataset.paginationStyle
    this.autoplay = this.dataset.autoplay === 'true'
    this.autoplaySpeed = Number(this.dataset.autoplaySpeed) * 1000

    this.maxBlocksMobile = parseInt(this.dataset.maxBlocksMobile, 10)

    if (!Number.isFinite(this.maxBlocksMobile) || this.maxBlocksMobile <= 0) {
      this.maxBlocksMobile = 0
    }

    this.centeredMobile =
      this.maxBlocksMobile > 0 && this.maxBlocksMobile <= this.mobileColumns

    this.allSlideElements = Array.from(this.slider.children)

    this.desktopMediaQuery = window.matchMedia('(min-width: 768px)')

    if (this.autoplay && this.enableSlider) {
      this.listeners.add(this, 'mouseenter', () => {
        this.stopAutoplay()
      })

      this.listeners.add(this, 'mouseleave', () => {
        this.startAutoplay()
      })

      this.listeners.add(this, 'focusin', () => {
        this.stopAutoplay()
      })

      this.listeners.add(this, 'focusout', () => {
        this.startAutoplay()
      })
    }

    this.listeners.add(
      this,
      'recently-viewed:loaded',
      () => {
        this.handleRecentlyViewedLoaded()
      },
    )

    this.handleBreakpointChange()

    this.listeners.add(
      this.desktopMediaQuery,
      'change',
      this.handleBreakpointChange.bind(this),
    )
  }

  handleRecentlyViewedLoaded () {
    if (!this.enableSlider) return

    this.allSlideElements = Array.from(this.slider.children)

    this.destroyCarousel()
    this.handleBreakpointChange()
  }

  applyMobileBlockLimit (isDesktop) {
    if (!this.maxBlocksMobile) return false

    const targetCount = isDesktop
      ? this.allSlideElements.length
      : Math.min(this.maxBlocksMobile, this.allSlideElements.length)

    if (this.slider.children.length === targetCount) return false

    this.slider.replaceChildren(
      ...this.allSlideElements.slice(0, targetCount),
    )

    return true
  }

  handleBreakpointChange () {
    const isDesktop = this.desktopMediaQuery.matches
    const blocksChanged = this.applyMobileBlockLimit(isDesktop)

    if (!this.enableSlider) {
      this.destroyCarousel()
      this.toggleControls(false)

      return
    }

    if (!isDesktop && (this.stackMobile || this.centeredMobile)) {
      this.destroyCarousel()
      this.toggleControls(false)

      return
    }

    if (blocksChanged) {
      this.destroyCarousel()
    }

    this.initialiseCarousel()
    this.toggleControls(true)
  }

  initialiseCarousel () {
    if (!this.enableSlider || this.carousel) return

    this.carousel = new window.KeenSlider(
      this.slider,
      {
        mode: 'snap',
        drag: true,
        dragSpeed: 1.5,

        slides: {
          perView: this.mobileColumns,
          spacing: this.columnGapMobile,
        },

        breakpoints: {
          '(min-width: 768px)': {
            slides: {
              perView: this.columns,
              spacing: this.columnGapDesktop,
            },
          },
        },

        created: (slider) => {
          this.slider.setAttribute('data-initialised', 'true')
          this.renderPagination(slider)
          this.updateControls(slider)
          this.startAutoplay()
        },

        slideChanged: (slider) => {
          this.updateControls(slider)
        },

        optionsChanged: (slider) => {
          this.renderPagination(slider)
          this.updateControls(slider)
        },

        updated: (slider) => {
          this.renderPagination(slider)
          this.updateControls(slider)
        },
      },
      [
        window.BAO.Carousel.plugins.setInitialised(),
        window.BAO.Carousel.plugins.addWheelControls('horizontal'),
        window.BAO.Carousel.plugins.limitDragToSingleSlide(
          () => !this.desktopMediaQuery.matches,
        ),
      ],
    )
  }

  goToPreviousSlide () {
    if (!this.carousel) return

    const details = this.carousel.track.details

    if (details.rel <= details.minIdx) {
      this.carousel.moveToIdx(details.maxIdx)

      return
    }

    this.carousel.prev()
  }

  goToNextSlide () {
    if (!this.carousel) return

    const details = this.carousel.track.details

    if (details.rel >= details.maxIdx) {
      this.carousel.moveToIdx(details.minIdx)

      return
    }

    this.carousel.next()
  }

  renderPagination (slider) {
    if (this.paginationStyle !== 'dots') return
    if (!this.dotsContainer) return

    const details = slider.track.details
    const numberOfDots = details.maxIdx - details.minIdx + 1

    this.dotsContainer.innerHTML = ''

    for (let index = 0; index < numberOfDots; index += 1) {
      const dot = document.createElement('button')

      dot.type = 'button'
      dot.className = 'sec-MultiColumnSlider_Dot'
      dot.dataset.collectionCarouselDot = ''
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`)

      if (index === details.rel) {
        dot.setAttribute('aria-current', 'true')
      }

      this.listeners.add(dot, 'click', () => {
        slider.moveToIdx(index)
      })

      this.dotsContainer.appendChild(dot)
    }
  }

  updateControls (slider) {
    const details = slider.track.details

    if (
      this.paginationStyle === 'dots' &&
      this.dotsContainer
    ) {
      const dots = this.dotsContainer.querySelectorAll(
        '[data-collection-carousel-dot]',
      )

      dots.forEach((dot, index) => {
        if (index === details.rel) {
          dot.setAttribute('aria-current', 'true')
        } else {
          dot.removeAttribute('aria-current')
        }
      })
    }

    if (
      this.paginationStyle === 'progress' &&
      this.progressBar
    ) {
      const totalSlides = details.maxIdx - details.minIdx + 1
      const currentSlide = details.rel - details.minIdx + 1

      const progress = (currentSlide / totalSlides) * 100

      this.progressBar.style.inlineSize = `${progress}%`
    }
  }

  toggleControls (showControls) {
    if (!this.controls) return

    this.controls.hidden = !showControls
  }

  startAutoplay () {
    if (
      !this.enableSlider ||
      !this.autoplay ||
      !this.carousel
    ) return

    this.stopAutoplay()

    this.autoplayInterval = window.setInterval(() => {
      if (!this.carousel) return

      this.goToNextSlide()
    }, this.autoplaySpeed)
  }

  stopAutoplay () {
    if (!this.autoplayInterval) return

    window.clearInterval(this.autoplayInterval)

    this.autoplayInterval = null
  }

  destroyCarousel () {
    this.stopAutoplay()

    if (!this.carousel) return

    this.carousel.destroy()
    this.carousel = null

    if (this.slider) {
      this.slider.removeAttribute('data-initialised')
    }

    if (this.dotsContainer) {
      this.dotsContainer.innerHTML = ''
    }

    if (this.progressBar) {
      this.progressBar.style.inlineSize = '0%'
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback?.()

    this.destroyCarousel()
  }
}

if (!customElements.get('multi-column-slider')) {
  customElements.define('multi-column-slider', MultiColumnSlider)
}
